// Analytics Graph Types

export type BreakdownType = 'category' | 'employees';
export type ChartType = 'bar' | 'pie' | 'grouped';
export type GraphMode = 'current' | 'historical' | 'diffs';

// Diffs mode breakdown types
export type DiffsBreakdownType = 'category' | 'employees' | 'event_type' | 'event_category';

// Event category filter for diffs mode
export type EventCategoryFilter = 'all' | 'pricing' | 'packaging' | 'product';

// Count mode for diffs - all events or unique pages per period
export type DiffsCountMode = 'all' | 'unique';

// Chart mode for diffs - periods on X-axis, categories on X-axis, or events on X-axis
export type DiffsChartMode = 'periods' | 'categories' | 'events';

// Period granularity for diffs mode - quarterly (2025Q4) or yearly (2025Y)
export type PeriodGranularity = 'quarterly' | 'yearly';

export type HistoricalMetricType =
  | 'average_addons'
  | 'average_modalities'
  | 'average_thresholds';

export interface GraphConfig {
  mode?: GraphMode;           // 'current' or 'historical', defaults to 'current' for backward compat

  // Current mode fields
  breakdown?: BreakdownType;  // What to break down by (current mode only)

  // Both modes
  chartType: ChartType;       // How to visualize
  title?: string;             // Optional custom title

  // Historical mode fields
  metric?: HistoricalMetricType;  // Metric to track over time (historical mode only)
  selectedPeriods?: string[];     // Array of quarters (historical mode only, min 2)

  // Diffs mode fields
  diffsBreakdown?: DiffsBreakdownType;  // What to break down by (diffs mode only)
  diffsSelectedPeriods?: string[];       // Array of quarters (diffs mode only, min 1)
  diffsEventCategory?: EventCategoryFilter;  // Filter events by category (diffs mode only)
  diffsRequireAllPeriods?: boolean;      // Only include pages with diffs in all periods (default: true)
  diffsShiftQuarters?: boolean;          // Shift quarter labels back by 1 (default: true) - e.g. 2026Q1 displays as 2025Q4
  diffsCountMode?: DiffsCountMode;        // Count all events or unique pages per period (default: 'unique')
  diffsShowTrendLine?: boolean;           // Show/hide the total trend line overlay (default: true)
  diffsChartMode?: DiffsChartMode;        // 'periods' = X-axis is periods (default), 'categories' = X-axis is categories stacked by event type
  diffsPeriodGranularity?: PeriodGranularity;  // 'quarterly' (default) or 'yearly' for annual aggregation
  diffsGroupByCategory?: boolean;         // Group events by category (pricing/packaging/product) vs individual types
}

// Helper function to shift a quarter back by one
export function shiftQuarterBack(quarter: string): string {
  const match = quarter.match(/^(\d{4})Q([1-4])$/);
  if (!match) return quarter;

  let year = parseInt(match[1], 10);
  let q = parseInt(match[2], 10);

  if (q === 1) {
    year -= 1;
    q = 4;
  } else {
    q -= 1;
  }

  return `${year}Q${q}`;
}

export interface PeriodData {
  period: string;
  total: number;
  average: number;
  median: number;
  min: number;
  max: number;
  count?: number;  // Number of data points
  breakdown?: {
    by_category?: Record<string, unknown>;
    by_employees?: Record<string, unknown>;
  };
}

export interface GraphData {
  metric: string;
  periods: PeriodData[];
}

export interface GraphElementContent {
  config: GraphConfig;
  data?: GraphData;  // Cached data (optional)
  lastFetched?: string;  // ISO timestamp
}

// API Request/Response Types
export interface AnalyticsRequest {
  metric: 'company_count';  // Fixed metric for now
  periods: string[];        // Will use ['latest']
  options: {
    include_breakdowns: true;
    breakdown_by: [BreakdownType];
  };
  filters?: {
    category_id?: string[];  // Array of category UUIDs
    employees?: string[];     // Array of employee ranges
    [key: string]: any;
  };
}

// Raw API response structure
export interface AnalyticsAPIResponse {
  metric: string;
  periods: string[];
  filters: Record<string, unknown>;
  overall: Record<string, {
    total: number;
    avg: number;
    count: number;
    median: number;
    min: number;
    max: number;
  }>;
  by_category?: Record<string, unknown>;
  by_employees?: Record<string, unknown>;
  metadata?: {
    execution_time_ms: number;
    generated_at: string;
  };
}

// Transformed response (same as GraphData format)
export interface AnalyticsResponse {
  metric: string;
  periods: PeriodData[];
}

// Breakdown Metadata
export const BREAKDOWN_METADATA: Record<BreakdownType, {
  label: string;
  description: string;
}> = {
  category: {
    label: 'Companies by Category',
    description: 'Number of companies in each category'
  },
  employees: {
    label: 'Companies by Employee Count',
    description: 'Number of companies by employee range'
  }
};

// Historical Metric Metadata
export const HISTORICAL_METRIC_METADATA: Record<HistoricalMetricType, {
  label: string;
  description: string;
  yAxisLabel: string;
}> = {
  average_addons: {
    label: 'Average Add-ons per Page',
    description: 'Track how add-on offerings change over time',
    yAxisLabel: 'Avg Add-ons'
  },
  average_modalities: {
    label: 'Average Modalities per Company',
    description: 'Track pricing model diversity over time',
    yAxisLabel: 'Avg Modalities'
  },
  average_thresholds: {
    label: 'Average Thresholds per Plan',
    description: 'Track pricing complexity over time',
    yAxisLabel: 'Avg Thresholds'
  }
};

// Diffs Breakdown Metadata
export const DIFFS_BREAKDOWN_METADATA: Record<DiffsBreakdownType, {
  label: string;
  description: string;
}> = {
  category: {
    label: 'By Company Category',
    description: 'Group events by company category (SaaS, Fintech, etc.)'
  },
  employees: {
    label: 'By Company Size',
    description: 'Group events by employee count ranges'
  },
  event_type: {
    label: 'By Event Type',
    description: 'Group by event type (price_increased, plan_added, etc.)'
  },
  event_category: {
    label: 'By Event Category',
    description: 'Group by event category (Pricing, Packaging, Product)'
  }
};

// Diffs Analytics API Types
export interface DiffsAnalyticsRequest {
  periods: string[];
  filters?: {
    category_id?: string | string[];
    slug?: string;
    employees?: string | string[];
    archived?: boolean;
  };
  options?: {
    include_breakdowns?: boolean;
    breakdown_by?: DiffsBreakdownType[];
    require_all_periods?: boolean;
    count_mode?: DiffsCountMode;  // 'all' = count every event, 'unique' = count unique pages per period (default)
  };
}

export interface DiffsPeriodData {
  total_diffs: number;
  unique_companies: number;
  total_events: number;
}

export interface DiffsBreakdownData {
  count: number;
}

export interface DiffsAnalyticsResponse {
  periods: string[];
  overall: Record<string, DiffsPeriodData>;
  by_category?: Record<string, Record<string, DiffsBreakdownData>>;
  by_employees?: Record<string, Record<string, DiffsBreakdownData>>;
  by_event_type?: Record<string, Record<string, DiffsBreakdownData>>;
  by_event_category?: Record<string, Record<string, DiffsBreakdownData>>;
  options?: {
    require_all_periods?: boolean;
  };
  metadata: {
    execution_time_ms: number;
    generated_at: string;
    pages_with_all_periods?: number;
    pages_excluded?: number;
  };
}

// Event type to category mapping (24 event types -> 3 categories)
export const EVENT_TYPE_CATEGORIES: Record<string, EventCategoryFilter> = {
  // Pricing events (8)
  price_increased: 'pricing',
  price_decreased: 'pricing',
  price_hidden: 'pricing',
  price_revealed: 'pricing',
  discount_added: 'pricing',
  discount_removed: 'pricing',
  discount_changed: 'pricing',
  pricing_restructured: 'pricing',
  // Packaging events (13)
  plan_added: 'packaging',
  plan_removed: 'packaging',
  plan_renamed: 'packaging',
  pricing_metric_changed: 'packaging',
  threshold_added: 'packaging',
  threshold_removed: 'packaging',
  capacity_increased: 'packaging',
  capacity_decreased: 'packaging',
  trial_changed: 'packaging',
  freemium_changed: 'packaging',
  addon_added: 'packaging',
  addon_removed: 'packaging',
  addon_changed: 'packaging',
  // Product events (4)
  feature_added: 'product',
  feature_removed: 'product',
  feature_changed: 'product',
  positioning_changed: 'product',
};

// All event types in display order
export const ALL_EVENT_TYPES = [
  'price_increased', 'price_decreased', 'price_hidden', 'price_revealed',
  'discount_added', 'discount_removed', 'discount_changed', 'pricing_restructured',
  'plan_added', 'plan_removed', 'plan_renamed', 'pricing_metric_changed',
  'threshold_added', 'threshold_removed', 'capacity_increased', 'capacity_decreased',
  'trial_changed', 'freemium_changed',
  'addon_added', 'addon_removed', 'addon_changed',
  'feature_added', 'feature_removed', 'feature_changed',
  'positioning_changed',
] as const;

// Metadata for event category filter
export const EVENT_CATEGORY_METADATA: Record<EventCategoryFilter, {
  label: string;
  description: string;
  color: string;
}> = {
  all: { label: 'All Events', description: 'Show all event types', color: '#6366f1' },
  pricing: { label: 'Pricing Events', description: 'Price changes, discounts, restructuring', color: '#10b981' },
  packaging: { label: 'Packaging Events', description: 'Plans, add-ons, thresholds, capacity', color: '#f59e0b' },
  product: { label: 'Product Events', description: 'Feature additions, removals, changes', color: '#ec4899' },
};

// Diffs Pages Drilldown API Types
export interface DiffsPagesRequest {
  period: string;
  breakdown_type: DiffsBreakdownType;
  breakdown_value: string;
  filters?: {
    category_id?: string | string[];
    slug?: string;
    employees?: string | string[];
    archived?: boolean;
  };
  pagination?: {
    page?: number;
    per_page?: number;
  };
  sort?: {
    field: 'company_name' | 'event_count' | 'page_name';
    direction: 'asc' | 'desc';
  };
  // For require_all_periods filtering
  all_periods?: string[];
  require_all_periods?: boolean;
  count_mode?: DiffsCountMode;  // Should match main analytics request
}

export interface DiffsPagesEventAnnotation {
  type: string;
  compare_viewer_url?: string;
  before_viewer_url?: string;
  after_viewer_url?: string;
  before_annotated_urls?: string[];
  after_annotated_urls?: string[];
  before_cropped_urls?: string[];
  after_cropped_urls?: string[];
  before_marker?: { x: number; y: number };
  after_marker?: { x: number; y: number };
}

export interface DiffsPagesEvent {
  event_id: string;
  event_type: string;
  description: string;
  created_at: string;
  annotation?: DiffsPagesEventAnnotation;
}

export interface DiffsPagesItem {
  diff_id: string;
  title: string;
  before_scrape_id: string;
  after_scrape_id: string;
  created_at: string;
  page_id: string;
  page_name: string;
  page_url: string;
  company_id: string;
  company_slug: string;
  company_name: string;
  logo_url?: string;
  category?: string;
  employees?: string;
  event_count: number;
  events: DiffsPagesEvent[];
  combined_annotation?: {
    compare_viewer_url?: string;
    before_viewer_url?: string;
    after_viewer_url?: string;
  };
}

export interface DiffsPagesResponse {
  period: string;
  breakdown_type: DiffsBreakdownType;
  breakdown_value: string;
  pagination: {
    page: number;
    per_page: number;
    total_pages: number;
    total_count: number;
  };
  diffs: DiffsPagesItem[];
  metadata: {
    execution_time_ms: number;
    generated_at: string;
  };
}

// Diffs Crosstab API Types (Public API)
export interface DiffsCrosstabRequest {
  periods: string[];
  group_by: 'category' | 'employees';
  filters?: {
    category_id?: string | string[];
    employees?: string | string[];
    archived?: boolean;
  };
  options?: {
    limit?: number;
  };
}

export interface DiffsCrosstabEventCounts {
  // Pricing (8)
  price_increased: number;
  price_decreased: number;
  price_hidden: number;
  price_revealed: number;
  discount_added: number;
  discount_removed: number;
  discount_changed: number;
  pricing_restructured: number;
  // Plans (10)
  plan_added: number;
  plan_removed: number;
  plan_renamed: number;
  pricing_metric_changed: number;
  threshold_added: number;
  threshold_removed: number;
  capacity_increased: number;
  capacity_decreased: number;
  trial_changed: number;
  freemium_changed: number;
  // Add-ons (3)
  addon_added: number;
  addon_removed: number;
  addon_changed: number;
  // Features (4)
  feature_added: number;
  feature_removed: number;
  feature_changed: number;
  positioning_changed: number;
}

export interface DiffsCrosstabDataItem {
  // Handle both API response formats
  category: string;           // Name or UUID depending on API version
  category_id?: string;       // UUID (actual API)
  category_name?: string;     // Name (per docs)
  employees?: string;         // Employee range (when group_by='employees')
  total: number;
  event_counts: DiffsCrosstabEventCounts;
}

export interface DiffsCrosstabResponse {
  periods: string[];
  group_by: 'category' | 'employees';
  data: DiffsCrosstabDataItem[];
  totals: {
    groups: number;
    events: number;
    companies: number;
    pages: number;
  };
  metadata: {
    execution_time_ms: number;
    generated_at: string;
  };
}
